/**
 * Group detail: members by size/kind; add size product; apply shared variant/package.
 */
import { useMemo, useState } from "react";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingModelsGroupApplyShared } from "./ManufacturingModelsGroupApplyShared";
import { ManufacturingModelsGroupRenamePanel } from "./ManufacturingModelsGroupRenamePanel";
import { groupRecipeMembers } from "@/lib/manufacturing/groupMembers";
import { sizeLabelOrNull } from "@/lib/manufacturing/seriesWizardPlan";
import {
  createManufacturingRecipe,
  updateManufacturingRecipe,
} from "@/lib/manufacturing/recipesClient";
import type { MfgRecipeGroupSummary } from "@/lib/manufacturing/recipeGroupsClient";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

export function ManufacturingModelsGroupDetailModal({
  group,
  recipes,
  canEditCode = false,
  onClose,
  onChanged,
  onOpenSeriesWizard,
  onAuthorGap,
  onPickRecipe,
}: {
  group: MfgRecipeGroupSummary;
  recipes: readonly MfgRecipeSummary[];
  /** Org-admin: allow member recipe code retitle when renaming the series. */
  canEditCode?: boolean;
  onClose: () => void;
  onChanged: (message?: string) => void;
  onOpenSeriesWizard: () => void;
  onAuthorGap: () => void;
  onPickRecipe: (id: string) => void;
}) {
  const sections = useMemo(
    () => groupRecipeMembers(recipes, group.id),
    [recipes, group.id],
  );
  const [sizeRaw, setSizeRaw] = useState("");
  const [linkId, setLinkId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const ungroupedProducts = useMemo(
    () =>
      recipes.filter(
        (r) => r.kind === "product" && r.is_active && !r.recipe_group_id,
      ),
    [recipes],
  );

  async function addSizeProduct() {
    const sizeLabel = sizeLabelOrNull(sizeRaw);
    if (!sizeLabel) {
      setError("Enter a size like 10x12");
      return;
    }
    setBusy(true);
    setError(null);
    const code = `${group.code}-${sizeLabel}`;
    const r = await createManufacturingRecipe({
      code,
      name: `${group.name} ${sizeLabel}`,
      kind: "product",
      tags: [`size:${sizeLabel}`],
      recipe_group_id: group.id,
      needs_review: true,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSizeRaw("");
    setToast(`Created ${r.data.recipe.code}`);
    onChanged(`Created ${r.data.recipe.code}`);
  }

  async function linkExisting() {
    if (!linkId) return;
    setBusy(true);
    setError(null);
    const r = await updateManufacturingRecipe({
      recipe_id: linkId,
      recipe_group_id: group.id,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setLinkId("");
    setToast(`Linked ${r.data.recipe.code} into group`);
    onChanged(`Linked ${r.data.recipe.code}`);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-950">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">
              {group.name}
            </h2>
            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
              Group <span className="font-mono text-xs">{group.code}</span> — members by size /
              kind
            </p>
          </div>
          <button type="button" className={ghostBtnClass} onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
              {error}
            </div>
          ) : null}
          {toast ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100">
              {toast}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <button type="button" className={mfgSecondaryBtnClass} onClick={onOpenSeriesWizard}>
              Series wizard
            </button>
            <button type="button" className={mfgSecondaryBtnClass} onClick={onAuthorGap}>
              Author gap (sandbox)
            </button>
          </div>

          <ManufacturingModelsGroupRenamePanel
            group={group}
            recipes={recipes}
            canEditCode={canEditCode}
            busy={busy}
            setBusy={setBusy}
            onError={setError}
            onDone={(msg) => {
              setToast(msg);
              onChanged(msg);
            }}
          />

          <ManufacturingModelsGroupApplyShared
            recipeGroupId={group.id}
            recipes={recipes}
            onDone={(msg) => {
              setToast(msg);
              onChanged(msg);
            }}
          />

          <div className="space-y-2 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
              Add size product
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="block min-w-[8rem] flex-1 text-sm">
                <span className="text-slate-600 dark:text-slate-300">Size</span>
                <input
                  className={`${inputClass} mt-1`}
                  value={sizeRaw}
                  onChange={(e) => setSizeRaw(e.target.value)}
                  placeholder="size (W×L)"
                  autoComplete="off"
                />
              </label>
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={busy}
                onClick={() => void addSizeProduct()}
              >
                Add
              </button>
            </div>
            {ungroupedProducts.length > 0 ? (
              <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-2 dark:border-slate-800">
                <label className="block min-w-[12rem] flex-1 text-sm">
                  <span className="text-slate-600 dark:text-slate-300">Link existing product</span>
                  <select
                    className={`${inputClass} mt-1`}
                    value={linkId}
                    onChange={(e) => setLinkId(e.target.value)}
                  >
                    <option value="">Select ungrouped…</option>
                    {ungroupedProducts.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.code})
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className={mfgSecondaryBtnClass}
                  disabled={busy || !linkId}
                  onClick={() => void linkExisting()}
                >
                  Link
                </button>
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Members ({sections.reduce((n, s) => n + s.recipes.length, 0)})
            </div>
            {sections.length === 0 ? (
              <p className="text-sm text-slate-500">No recipes in this group yet.</p>
            ) : (
              sections.map((section) => (
                <div
                  key={`${section.kind}-${section.sizeLabel ?? "_"}`}
                  className="rounded-xl border border-slate-200 dark:border-slate-700"
                >
                  <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800">
                    {section.kind}
                    {section.sizeLabel ? ` · ${section.sizeLabel}` : " · (no size)"}
                  </div>
                  <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                    {section.recipes.map((r) => (
                      <li
                        key={r.id}
                        className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                      >
                        <span className="min-w-0 truncate">
                          <span className="font-medium text-slate-900 dark:text-slate-50">
                            {r.name}
                          </span>
                          <span className="text-slate-500"> · {r.code}</span>
                          {!r.is_active ? (
                            <span className="ml-1 text-xs text-amber-700">inactive</span>
                          ) : null}
                        </span>
                        <button
                          type="button"
                          className={ghostBtnClass}
                          onClick={() => {
                            onPickRecipe(r.id);
                            onClose();
                          }}
                        >
                          Open
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
